import type { PlausibilityPrior } from "@sitescene/types";

import { useState } from "react";

import { type PriorWriteInput } from "@/features/plausibility/use-priors";

import { type PriorRowEdit } from "./prior-columns";
import {
	type PriorValueDraft,
	valueDraftFromPrior,
	valueDraftToWriteInput,
} from "./prior-draft";

interface RowEditState {
	draft: PriorValueDraft;
	row: PlausibilityPrior;
}

/**
 * Tracks which prior row is being edited in place and its unsaved value.
 * `onSave` receives the row and the write that keeps its identity; the row
 * closes when the returned promise resolves and stays open if it rejects,
 * so the caller can surface the error next to it.
 */
export const usePriorRowEdit = (
	onSave: (row: PlausibilityPrior, input: PriorWriteInput) => Promise<unknown>,
) => {
	const [state, setState] = useState<null | RowEditState>(null);

	const edit: null | PriorRowEdit = state
		? {
				draft: state.draft,
				id: state.row.id,
				onCancel: () => setState(null),
				onChange: (changes) =>
					setState((current) =>
						current
							? { ...current, draft: { ...current.draft, ...changes } }
							: current,
					),
				onSave: () =>
					onSave(
						state.row,
						valueDraftToWriteInput(state.row, state.draft),
					).then(
						() => setState(null),
						() => undefined,
					),
			}
		: null;

	return {
		edit,
		startEdit: (row: PlausibilityPrior) =>
			setState({ draft: valueDraftFromPrior(row), row }),
	};
};
